<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import HighlightForm from './HighlightForm.vue';
import type { Highlight, Underline } from '@ilteducation/annotations-types';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { HighlightWithText } from '../../stores/highlights';

library.add(faXmark);

const showDialog = defineModel<boolean>({ default: true });

type Emits = {
  (e: 'save', color: string, type: Highlight['type'] | Underline['type'], comment?: string): void;
  (e: 'remove'): void;
};
const emits = defineEmits<Emits>();

type Props = {
  highlight?:
    | HighlightWithText
    | (Partial<Pick<HighlightWithText, 'id' | 'text' | 'color' | 'comment'>> & {
        type?: Highlight['type'] | Underline['type'];
      }); // If provided, we're in edit mode
  isSaving?: boolean;
  isRemoving?: boolean;
};
const props = defineProps<Props>();
const { highlight, isSaving, isRemoving } = toRefs(props);

const { t } = useLocale();
const { xs } = useDisplay();

const state = computed<'edit' | 'new'>(() => (highlight.value?.id ? 'edit' : 'new'));

const title = computed(() => {
  return state.value === 'edit' ? t('label_edit_highlight') : t('label_new_highlight');
});

const highlightComment = computed(() => {
  const comment = highlight.value?.comment;
  return comment && 'type' in comment && comment.type === 'text' && 'content' in comment ? comment.content : undefined;
});
</script>

<template>
  <v-dialog v-model="showDialog" max-width="560" :fullscreen="xs" :persistent="true" @keydown.esc="showDialog = false">
    <v-card class="highlight-dialog">
      <v-card-title class="d-flex align-center justify-end position-relative">
        <div class="text-h5 text-center w-100 position-absolute left-0 right-0">
          {{ title }}
        </div>
        <v-btn
          size="48"
          variant="text"
          :title="t('label_close')"
          :aria-label="t('label_close')"
          @click="showDialog = false"
        >
          <FontAwesomeIcon size="sm" icon="far fa-xmark" />
        </v-btn>
      </v-card-title>
      <v-divider class="mb-2"></v-divider>

      <HighlightForm
        :text="highlight?.text"
        :color="highlight?.color"
        :type="highlight?.type"
        :comment="highlightComment"
        :state="state"
        :is-saving="isSaving"
        :is-removing="isRemoving"
        @save="(color, type, comment) => emits('save', color, type, comment)"
        @remove="emits('remove')"
        @cancel="showDialog = false"
      >
        <template #highlightMessaging="{ highlightedText, state: slotState }">
          <slot name="highlightMessaging" :highlightedText="highlightedText" :state="slotState"></slot>
        </template>
      </HighlightForm>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.highlight-dialog {
  --highlight-ring-color: rgb(var(--v-theme-on-surface));
}

:deep(.v-card) {
  padding-top: env(safe-area-inset-top, 0px) !important;
  padding-bottom: env(safe-area-inset-bottom, 0px) !important;
}
</style>
